<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Live Chat</span>
            </nav>
            <h1 class="page-title">AI Assistant</h1>
            <p class="page-subtitle">Get instant help with county services and payments</p>
          </div>
        </div>
      </section>

      <!-- Chat Section -->
      <section class="chat-section">
        <div class="container">
          <div class="chat-layout">
            <!-- Main Chat Area -->
            <div class="chat-main">
              <div class="chat-card">
                <!-- Chat Header -->
                <div class="chat-header">
                  <div class="assistant-info">
                    <div class="assistant-avatar">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                        <circle cx="7.5" cy="14.5" r="1.5"></circle>
                        <circle cx="16.5" cy="14.5" r="1.5"></circle>
                      </svg>
                    </div>
                    <div class="assistant-details">
                      <h3>County Assistant</h3>
                      <span class="status online">
                        <span class="status-dot"></span>
                        Online
                      </span>
                    </div>
                  </div>
                  <button class="clear-btn" @click="clearChat" title="Clear conversation">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>

                <!-- Messages Container -->
                <div class="messages-container" ref="messagesContainer">
                  <!-- Welcome State -->
                  <div v-if="messages.length === 0" class="welcome-state">
                    <div class="welcome-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                        <circle cx="7.5" cy="14.5" r="1.5"></circle>
                        <circle cx="16.5" cy="14.5" r="1.5"></circle>
                      </svg>
                    </div>
                    <h3>Hello! I'm your County Assistant</h3>
                    <p>I can help you with information about county services, payments, permits, and more. How can I assist you today?</p>

                    <div class="quick-questions">
                      <p class="quick-label">Quick questions:</p>
                      <div class="question-chips">
                        <button
                            v-for="(question, index) in quickQuestions"
                            :key="index"
                            class="question-chip"
                            @click="sendQuickQuestion(question)"
                        >
                          {{ question }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Messages -->
                  <div
                      v-for="(message, index) in messages"
                      :key="index"
                      class="message"
                      :class="message.role"
                  >
                    <div class="message-avatar" v-if="message.role === 'assistant'">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                      </svg>
                    </div>
                    <div class="message-content">
                      <div class="message-text">{{ message.content }}</div>

                      <!-- Data Table -->
                      <div v-if="message.data && message.data.length > 0" class="message-table">
                        <div class="table-wrapper">
                          <table>
                            <thead>
                            <tr>
                              <th v-for="key in Object.keys(message.data[0])" :key="key">
                                {{ formatColumnName(key) }}
                              </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(row, rowIndex) in message.data" :key="rowIndex">
                              <td v-for="(value, key) in row" :key="key">
                                {{ formatDataValue(value) }}
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <span class="message-time">{{ formatTime(message.timestamp) }}</span>
                    </div>
                    <div class="message-avatar user-avatar" v-if="message.role === 'user'">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                  </div>

                  <!-- Typing Indicator -->
                  <div v-if="loading" class="message assistant">
                    <div class="message-avatar">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                      </svg>
                    </div>
                    <div class="message-content">
                      <div class="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Input Area -->
                <div class="chat-input-area">
                  <div class="input-wrapper">
                    <input
                        type="text"
                        v-model="userInput"
                        class="chat-input"
                        placeholder="Type your message..."
                        @keyup.enter="sendMessage"
                        :disabled="loading"
                        ref="chatInput"
                    />
                    <button
                        class="send-btn"
                        @click="sendMessage"
                        :disabled="!userInput.trim() || loading"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    </button>
                  </div>
                  <p class="input-hint">Press Enter to send • AI-powered assistance • Follow-up questions supported</p>
                </div>
              </div>
            </div>

            <!-- Sidebar -->
            <aside class="chat-sidebar">
              <!-- Suggested Topics -->
              <div class="sidebar-card">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  Common Topics
                </h4>
                <div class="topic-list">
                  <button
                      v-for="(topic, index) in suggestedTopics"
                      :key="index"
                      class="topic-btn"
                      @click="sendQuickQuestion(topic)"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                    {{ topic }}
                  </button>
                </div>
              </div>

              <!-- About Card -->
              <div class="sidebar-card about-card">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  About Assistant
                </h4>
                <p>Our AI assistant can help you with:</p>
                <ul>
                  <li>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Payment inquiries
                  </li>
                  <li>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Service information
                  </li>
                  <li>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Permit guidance
                  </li>
                  <li>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Follow-up questions
                  </li>
                </ul>
              </div>

              <!-- Contact Card -->
              <div class="sidebar-card contact-card">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  Need Human Help?
                </h4>
                <p>Our support team is available during business hours.</p>
                <a href="/contact" class="contact-link">
                  Contact Support
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: 'LiveChat',
  components: { HeaderComponent, FooterComponent },

  data() {
    return {
      userInput: '',
      messages: [],
      loading: false,
      chatContext: null, // Store context from last response for follow-ups
      quickQuestions: [
        'How do I pay my bill?',
        'What services are available?',
        'How do I get a business permit?',
        'Check my land rates'
      ],
      suggestedTopics: [
        'How to pay via M-Pesa',
        'Business permit application',
        'Land rates payment',
        'Print my receipt',
        'Check payment status',
        'Parking fees information',
        'Contact county offices',
        'Operating hours'
      ]
    };
  },

  mounted() {
    this.$refs.chatInput?.focus();
  },

  methods: {
    /**
     * Send message with conversation history for context
     */
    async sendMessage() {
      if (!this.userInput.trim() || this.loading) return;

      const userMessage = {
        role: 'user',
        content: this.userInput,
        timestamp: new Date(),
        data: null
      };

      this.messages.push(userMessage);
      const userQuery = this.userInput;
      this.userInput = '';
      this.loading = true;

      this.scrollToBottom();

      try {
        // Build conversation history for context-aware responses
        const conversationHistory = this.buildConversationHistory();

        const response = await this.queryAI(userQuery, conversationHistory);

        const assistantMessage = {
          role: 'assistant',
          content: response.answer || 'I apologize, but I couldn\'t process that request. Please try again or rephrase your question.',
          timestamp: new Date(),
          data: response.tableData || null
        };

        this.messages.push(assistantMessage);

        // Store context for next query (contains LR numbers, business IDs, etc.)
        if (response.context) {
          this.chatContext = response.context;
        }
      } catch (error) {
        const errorMessage = {
          role: 'assistant',
          content: 'I\'m sorry, I encountered an error. Please try again or contact our support team for assistance.',
          timestamp: new Date(),
          data: null
        };
        this.messages.push(errorMessage);
        console.error('Chat error:', error);
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },

    /**
     * Build conversation history array to send to backend
     * This enables follow-up questions like "who is the owner?"
     */
    buildConversationHistory() {
      // Get last 10 messages (5 exchanges) for context
      const recentMessages = this.messages.slice(-10);

      return recentMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
    },

    /**
     * Query AI with conversation history for context-aware responses
     */
    async queryAI(query, conversationHistory = []) {
      const data = new FormData();
      data.append("function", "queryAIAssistant");
      data.append("query", query);
      data.append("sessionNames", sessionStorage.getItem("sessionNames") || '');
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo") || '');

      // Send conversation history as JSON for follow-up context
      data.append("conversation_history", JSON.stringify(conversationHistory));

      // Send previous context (contains LR numbers, business IDs from last query)
      if (this.chatContext) {
        data.append("previous_context", JSON.stringify(this.chatContext));
      }

      try {
        const response = await execute(data, biller);
        const aiResponse = response.data;

        if (aiResponse.success && aiResponse.data) {
          const aiData = aiResponse.data;
          return {
            success: aiData.success,
            answer: aiData.data?.answer || aiData.answer || 'No response received',
            tableData: aiData.data?.tableData || aiData.tableData || null,
            context: aiData.data?.context || aiData.context || null,
            error: aiData.error || null
          };
        } else {
          throw new Error(aiResponse.error || 'Failed to get response');
        }
      } catch (error) {
        console.error('AI Query Error:', error);
        throw error;
      }
    },

    /**
     * Send a quick question from preset options
     */
    sendQuickQuestion(question) {
      this.userInput = question;
      this.sendMessage();
    },

    /**
     * Clear chat history and context - starts fresh conversation
     */
    clearChat() {
      if (this.messages.length === 0) return;
      if (confirm('Clear all messages?')) {
        this.messages = [];
        this.chatContext = null; // Also clear context
      }
    },

    /**
     * Scroll chat container to bottom after new message
     */
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    /**
     * Format timestamp for display
     */
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    /**
     * Format column name for table display
     */
    formatColumnName(name) {
      return name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    /**
     * Format data value for table display
     */
    formatDataValue(value) {
      if (value === null || value === undefined) return '-';

      if (typeof value === 'number') {
        if (value > 999) {
          return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value);
        }
        return value.toLocaleString('en-KE');
      }

      if (value instanceof Date) {
        return value.toLocaleDateString('en-KE');
      }

      return String(value);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.page-wrapper { font-family: 'Plus Jakarta Sans', sans-serif; background: #f5f7fa; min-height: 100vh; }
.main-content { padding-bottom: 80px; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 50%, #0a3459 100%); padding: 48px 0 80px; position: relative; overflow: hidden; }
.page-hero::before { content: ''; position: absolute; top: 0; right: 0; width: 500px; height: 500px; background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%); transform: translate(30%, -30%); }
.hero-content { text-align: center; position: relative; z-index: 1; }

.breadcrumb-nav { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; font-size: 14px; }

.page-title { font-size: 36px; font-weight: 800; color: white; margin: 0 0 12px; letter-spacing: -0.5px; }
.page-subtitle { font-size: 16px; color: rgba(255,255,255,0.8); margin: 0; }

/* Chat Section */
.chat-section { margin-top: -50px; position: relative; z-index: 10; padding-bottom: 40px; }
.chat-layout { display: grid; grid-template-columns: 1fr 300px; gap: 24px; align-items: start; }

/* Chat Card */
.chat-card { background: white; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); overflow: hidden; display: flex; flex-direction: column; height: 600px; }

.chat-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-bottom: 1px solid #e2e8f0; }
.assistant-info { display: flex; align-items: center; gap: 14px; }
.assistant-avatar { width: 48px; height: 48px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; }
.assistant-details h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
.status { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #059669; font-weight: 500; }
.status-dot { width: 8px; height: 8px; background: #10b981; border-radius: 50%; animation: pulse 2s infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

.clear-btn { width: 40px; height: 40px; background: white; border: 1px solid #e2e8f0; border-radius: 10px; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.clear-btn:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }

/* Messages Container */
.messages-container { flex: 1; overflow-y: auto; padding: 24px; display: flex; flex-direction: column; gap: 16px; }

/* Welcome State */
.welcome-state { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 40px 20px; height: 100%; }
.welcome-icon { width: 100px; height: 100px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #2563eb; margin-bottom: 24px; }
.welcome-state h3 { font-size: 22px; font-weight: 700; color: #1e293b; margin: 0 0 12px; }
.welcome-state > p { font-size: 14px; color: #64748b; margin: 0 0 32px; max-width: 400px; line-height: 1.6; }

.quick-questions { width: 100%; max-width: 500px; }
.quick-label { font-size: 13px; font-weight: 600; color: #64748b; margin: 0 0 12px; }
.question-chips { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.question-chip { padding: 10px 18px; background: #f1f5f9; border: 2px solid #e2e8f0; border-radius: 25px; font-size: 13px; font-weight: 500; color: #475569; cursor: pointer; transition: all 0.2s; }
.question-chip:hover { background: #0f4c81; border-color: #0f4c81; color: white; transform: translateY(-2px); }

/* Messages */
.message { display: flex; gap: 12px; max-width: 85%; }
.message.user { flex-direction: row-reverse; margin-left: auto; }
.message.assistant { margin-right: auto; }

.message-avatar { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.message.assistant .message-avatar { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; }
.message.user .user-avatar { background: linear-gradient(135deg, #059669 0%, #047857 100%); color: white; }

.message-content { display: flex; flex-direction: column; gap: 6px; }
.message-text { padding: 14px 18px; border-radius: 16px; font-size: 14px; line-height: 1.6; }
.message.assistant .message-text { background: #f1f5f9; color: #1e293b; border-bottom-left-radius: 4px; }
.message.user .message-text { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; border-bottom-right-radius: 4px; }

.message-time { font-size: 11px; color: #94a3b8; }
.message.user .message-time { text-align: right; }

/* Message Table */
.message-table { margin-top: 12px; background: white; border-radius: 12px; overflow: hidden; border: 1px solid #e2e8f0; }
.table-wrapper { max-height: 200px; overflow: auto; }
.message-table table { width: 100%; border-collapse: collapse; font-size: 12px; }
.message-table th { background: #f8fafc; padding: 10px 12px; text-align: left; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0; white-space: nowrap; position: sticky; top: 0; }
.message-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #64748b; white-space: nowrap; }
.message-table tr:last-child td { border-bottom: none; }
.message-table tr:hover td { background: #f8fafc; }

/* Typing Indicator */
.typing-indicator { display: flex; gap: 4px; padding: 14px 18px; background: #f1f5f9; border-radius: 16px; border-bottom-left-radius: 4px; }
.typing-indicator span { width: 8px; height: 8px; background: #94a3b8; border-radius: 50%; animation: typing 1.4s infinite; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-8px); } }

/* Input Area */
.chat-input-area { padding: 20px 24px; background: #f8fafc; border-top: 1px solid #e2e8f0; }
.input-wrapper { display: flex; gap: 12px; }
.chat-input { flex: 1; padding: 14px 18px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-family: inherit; transition: all 0.2s; background: white; }
.chat-input:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1); }
.chat-input:disabled { background: #f1f5f9; cursor: not-allowed; }
.chat-input::placeholder { color: #94a3b8; }

.send-btn { width: 52px; height: 52px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border: none; border-radius: 12px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0; }
.send-btn:hover:not(:disabled) { transform: scale(1.05); box-shadow: 0 4px 15px rgba(15, 76, 129, 0.4); }
.send-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.input-hint { font-size: 12px; color: #94a3b8; margin: 10px 0 0; text-align: center; }

/* Sidebar */
.chat-sidebar { display: flex; flex-direction: column; gap: 20px; position: sticky; top: 100px; }

.sidebar-card { background: white; border-radius: 16px; padding: 24px; box-shadow: 0 4px 15px rgba(0,0,0,0.08); }
.sidebar-card h4 { display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 700; color: #1e293b; margin: 0 0 16px; }

.topic-list { display: flex; flex-direction: column; gap: 8px; }
.topic-btn { display: flex; align-items: center; gap: 10px; padding: 12px 14px; background: #f8fafc; border: none; border-radius: 10px; font-size: 13px; font-weight: 500; color: #475569; cursor: pointer; transition: all 0.2s; text-align: left; width: 100%; }
.topic-btn:hover { background: #0f4c81; color: white; transform: translateX(4px); }
.topic-btn svg { color: #94a3b8; flex-shrink: 0; transition: color 0.2s; }
.topic-btn:hover svg { color: white; }

.about-card p { font-size: 13px; color: #64748b; margin: 0 0 12px; }
.about-card ul { list-style: none; padding: 0; margin: 0; }
.about-card li { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; padding: 8px 0; }
.about-card li svg { color: #10b981; flex-shrink: 0; }

.contact-card { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }
.contact-card h4 { color: #1e40af; }
.contact-card p { font-size: 13px; color: #3b82f6; margin: 0 0 16px; }
.contact-link { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #0f4c81; text-decoration: none; transition: gap 0.2s; }
.contact-link:hover { gap: 12px; }

/* Responsive */
@media (max-width: 1024px) {
  .chat-layout { grid-template-columns: 1fr; }
  .chat-sidebar { position: static; flex-direction: row; flex-wrap: wrap; }
  .sidebar-card { flex: 1; min-width: 250px; }
}

@media (max-width: 768px) {
  /* Slash the giant hero on mobile so the chat is above the fold. */
  .container { padding: 0 12px; }
  .page-hero { padding: 20px 0 50px; }
  .page-title { font-size: 22px; margin-bottom: 6px; }
  .hero-subtitle,
  .page-subtitle { font-size: 13px; }

  /* Full-viewport chat card — takes the whole screen minus the hero
     stub and the nav so the user isn't scrolling to reach the input. */
  .chat-section { margin-top: -30px; padding-bottom: 20px; }
  .chat-card {
    height: calc(100vh - 200px);
    min-height: 420px;
    max-height: none;
    border-radius: 14px;
  }
  .chat-header { padding: 12px 14px; gap: 10px; }
  .chat-header .assistant-info { gap: 10px; }
  .messages-container { padding: 14px; gap: 12px; }
  .chat-input-area { padding: 12px; gap: 8px; }
  .chat-input { font-size: 14px; padding: 10px 14px; min-height: 42px; }

  .message { max-width: 88%; }
  .message-avatar { width: 30px; height: 30px; }
  .message-avatar svg { width: 16px; height: 16px; }
  .message-text { padding: 10px 14px; font-size: 13.5px; line-height: 1.5; border-radius: 14px; }
  .message-time { font-size: 10px; }
  .message-table { margin-top: 8px; border-radius: 8px; }
  .message-table table { font-size: 11px; }
  .message-table th,
  .message-table td { padding: 6px 8px; }

  .chat-sidebar { flex-direction: column; gap: 12px; }
  .sidebar-card { min-width: auto; padding: 14px; }
  .welcome-state { padding: 16px 12px; }
  .welcome-icon { width: 60px; height: 60px; }
  .welcome-state h3 { font-size: 16px; margin-bottom: 6px; }
  .welcome-state p { font-size: 13px; line-height: 1.45; }
  .quick-questions h4 { font-size: 13px; }
}

@media (max-width: 480px) {
  .container { padding: 0 10px; }
  .page-hero { padding: 16px 0 40px; }
  .page-title { font-size: 19px; }
  .hero-subtitle,
  .page-subtitle { font-size: 12px; }

  .chat-card { height: calc(100vh - 180px); border-radius: 12px; }
  .chat-header { padding: 10px 12px; }
  .assistant-avatar { width: 34px; height: 34px; }
  .assistant-avatar svg { width: 18px; height: 18px; }
  .assistant-details h3 { font-size: 13px; }
  .assistant-details .status { font-size: 10.5px; }
  .messages-container { padding: 10px; }
  .message-text { font-size: 13px; padding: 9px 12px; }

  .chat-input-area { padding: 10px; }
  .chat-input { font-size: 13.5px; padding: 9px 12px; min-height: 40px; }
  .send-btn { width: 38px; height: 38px; }
  .send-btn svg { width: 16px; height: 16px; }

  /* Quick-question chips: horizontally scrollable strip instead of a
     vertical stack that pushes the input off-screen. */
  .question-chips {
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 6px;
    padding-bottom: 4px;
  }
  .question-chip {
    width: auto;
    text-align: center;
    white-space: nowrap;
    padding: 7px 12px;
    font-size: 12px;
    flex-shrink: 0;
  }

  .sidebar-card h4 { font-size: 13px; }
  .contact-card p { font-size: 11.5px; }
  .contact-link { font-size: 12.5px; }
}
</style>